import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { ModernChatContainerProps } from '../../../types/chat';
import VirtualizedMessageList from '../../molecules/chat/VirtualizedMessageList';
import InputToolbar, { InputToolbarMethods } from '../../molecules/chat/InputToolbar';
import AudioRecordingBar from '../../molecules/chat/AudioRecordingModal';
import PhotoModal from '../../molecules/PhotoModal';
import { useInfiniteMessages } from '../../../hooks/useInfiniteMessages';
import MessageData from '../../../data/MessageData';
import { sendReplyMessage } from '../../../services/MessageReactions';
import userDataState from '../../../atoms/userDataAtom';

const ModernChatContainer: React.FC<ModernChatContainerProps> = React.memo(({
  groupId,
  groupName,
  onHeaderUpdate,
}) => {
  // Recording state
  const [isRecordingBarVisible, setIsRecordingBarVisible] = useState(false);
  
  // Reply state
  const [replyToMessage, setReplyToMessage] = useState<any>(null);
  
  // Input toolbar ref for focusing
  const inputToolbarRef = useRef<InputToolbarMethods>(null);
  
  // Modal state for photo viewing
  const [photoModalVisible, setPhotoModalVisible] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);

  // Use our new infinite messages hook for real Firebase integration
  const {
    messages,
    loading,
    hasMore,
    loadMore,
    sendMessage,
    // markAsViewed, // TODO: Use when implementing read receipts
    // refreshMessages, // TODO: Add refresh functionality
  } = useInfiniteMessages({
    groupId,
    pageSize: 30,
    filter: null, // TODO: Add search functionality
  });

  // Get current user ID (this should come from your auth system)
  // TODO: Replace with proper user context/authentication
  const currentUserId = localStorage.getItem('uid') || 'temp-user-id';

  // Get current user data to determine user type for delete permissions
  const userData = useRecoilValue(userDataState) as any;
  // Check multiple possible property names for user type
  const currentUserType = userData?.userType || userData?.type || 'client'; // Default to client if no user data

  // Update header with group name and message count
  useEffect(() => {
    if (onHeaderUpdate && groupName) {
      const title = messages.length > 0 
        ? `${groupName} (${messages.length} messages)`
        : groupName;
      onHeaderUpdate(title);
    }
  }, [onHeaderUpdate, groupName, messages.length]);

  // Focus input when reply is set
  useEffect(() => {
    if (replyToMessage) {
      // Use a timeout to ensure the reply UI is rendered first
      const timer = setTimeout(() => {
        inputToolbarRef.current?.focus();
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [replyToMessage]);

  // Handle message interactions
  const handleMessageLongPress = useCallback((message: any) => {
    console.log('Message long pressed:', message);
    // TODO: Implement context menu actions
  }, []);

  const handleImagePress = useCallback((imageUrl: string, message: any) => {
    console.log('Image pressed:', imageUrl, message);
    setSelectedPhoto({ url: imageUrl, message });
    setPhotoModalVisible(true);
  }, []);

  const handleReactionPress = useCallback(async (message: any, emoji: string) => {
    console.log('Reaction pressed:', emoji, 'on message:', message.id);
    
    try {
      // Use the new toggle function which handles both add and remove
      const { toggleMessageReaction } = await import('../../../services/MessageReactions');
      const result = await toggleMessageReaction(message.id, emoji, groupId);
      
      if (result.reaction) {
        console.log('Successfully added reaction:', emoji);
      } else if (result.removedReaction) {
        console.log('Successfully removed reaction:', emoji);
      }
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      // Show user-friendly error message
      const action = message.reactions?.find((r: any) => r.uid === currentUserId && r.emoji === emoji) ? 'remove' : 'add';
      alert(`Failed to ${action} reaction. Please try again.`);
    }
  }, [currentUserId, groupId]);

  const handleReplyPress = useCallback((message: any) => {
    console.log('Reply pressed for message:', message.id);
    setReplyToMessage(message);
    
    // Focus the input field after a short delay to ensure the reply UI is rendered
    setTimeout(() => {
      inputToolbarRef.current?.focus();
    }, 100);
  }, []);

  const handleDeletePress = useCallback(async (message: any) => {
    // Confirm deletion
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await MessageData.deleteMessage(groupId, message.id);
        console.log('Successfully deleted message:', message.id);
      } catch (error) {
        console.error('Failed to delete message:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        alert(`Failed to delete message: ${errorMessage}`);
      }
    }
  }, [groupId]);

  const handleAttachmentPress = useCallback(() => {
    console.log('Attachment button pressed');
    // TODO: Implement file/media attachment
  }, []);

  // Handle message sending with error handling
  const handleSendText = useCallback(async (text: string) => {
    try {
      if (replyToMessage) {
        // Send as reply
        await sendReplyMessage(groupId, text, replyToMessage);
        setReplyToMessage(null); // Clear reply state after sending
      } else {
        // Send as regular message
        await sendMessage(text);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error; // Re-throw to show error in InputToolbar
    }
  }, [sendMessage, replyToMessage, groupId]);

  // Handle audio sending
  const handleSendAudio = useCallback(
    async (blob: Blob, buffer: ArrayBuffer, duration?: string) => {
      try {
        await (MessageData as any).sendAudioMessageToGroup(
          groupId,
          blob,
          buffer,
          duration,
        );
      } catch (error) {
        console.error('Failed to send audio message:', error);
        throw error; // Re-throw to show error in AudioRecordingModal
      }
    },
    [groupId],
  );

  // Handle start recording
  const handleStartRecording = useCallback(() => {
    setIsRecordingBarVisible(true);
  }, []);

  // Handle cancel recording
  const handleCancelRecording = useCallback(() => {
    setIsRecordingBarVisible(false);
  }, []);

  // Handle file uploads
  const handleSelectImage = useCallback(async (file: File) => {
    try {
      await (MessageData as any).sendImageMessageToGroup(groupId, file);
    } catch (error) {
      console.error('Failed to send image:', error);
      throw error;
    }
  }, [groupId]);

  const handleSelectVideo = useCallback(async (file: File) => {
    try {
      await (MessageData as any).sendVideoMessageToGroup(groupId, file);
    } catch (error) {
      console.error('Failed to send video:', error);
      throw error;
    }
  }, [groupId]);

  const handleSelectDocument = useCallback(async (file: File) => {
    try {
      await (MessageData as any).sendDocumentMessageToGroup(groupId, file);
    } catch (error) {
      console.error('Failed to send document:', error);
      throw error;
    }
  }, [groupId]);

  // Handle viewing messages (mark as read) - TODO: Use when implementing read receipts
  // const handleMarkAsViewed = useCallback(async (messageId: string) => {
  //   try {
  //     await markAsViewed(messageId);
  //   } catch (error) {
  //     console.error('Failed to mark message as viewed:', error);
  //   }
  // }, [markAsViewed]);

  // Handle canceling reply
  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden', // Prevent container overflow
      }}
    >
      {/* Chat Header - Enhanced with real message count */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(0, 119, 200, 0.1)',
          backgroundColor: '#ffffff',
          px: 3,
          py: 1.5, // Reduced from 2 to prevent height overflow
          flexShrink: 0,
        }}
        data-testid="chat-header"
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a2d66', m: 0 }}>
          {groupName || 'Modern Chat'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </Typography>
          {loading && (
            <Typography variant="caption" sx={{ color: '#2563EB' }}>
              • Loading...
            </Typography>
          )}
          {hasMore && (
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              • More available
            </Typography>
          )}
        </Box>
      </Box>

      {/* Virtualized Message List with Infinite Scroll */}
      <VirtualizedMessageList
        messages={messages}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
        onLoadMore={loadMore}
        onMessageLongPress={handleMessageLongPress}
        onImagePress={handleImagePress}
        onReactionPress={handleReactionPress}
        onReplyPress={handleReplyPress}
        onDeletePress={handleDeletePress}
        loading={loading}
        hasMore={hasMore}
      />

      {/* Input Toolbar */}
      <InputToolbar
        ref={inputToolbarRef}
        onSendText={handleSendText}
        onStartRecording={handleStartRecording}
        onAttachmentPress={handleAttachmentPress}
        onSelectImage={handleSelectImage}
        onSelectVideo={handleSelectVideo}
        onSelectDocument={handleSelectDocument}
        placeholder="Type a message..."
        groupId={groupId}
        replyToMessage={replyToMessage}
        onCancelReply={handleCancelReply}
      />

      {/* TODO: Add photo modal when we implement it */}
      <PhotoModal
        visible={photoModalVisible}
        photo={selectedPhoto}
        onClose={() => setPhotoModalVisible(false)}
      />

      {/* Audio Recording Bar */}
      <AudioRecordingBar
        show={isRecordingBarVisible}
        onSendAudio={handleSendAudio}
        onCancel={handleCancelRecording}
      />
    </Box>
  );
});

ModernChatContainer.displayName = 'ModernChatContainer';

export default ModernChatContainer;
